export type UsdLoadingHeightContext = {
	/** Keep adding props to thandle more loading height edge cases  */
	specificAccessVisible: boolean;
	isDraft: boolean;
	isPublishModalFlow: boolean;
	isEmbed: boolean;
};

export class UsdLoadingHeight {
	private _currentHeight: number = 0;

	private _observeTarget: HTMLElement | null = null;

	private _canRecordCurrentHeight: boolean = false;

	private _resizeObserver?: ResizeObserver;

	private _lastRecordedContentHeight: number = 0;

	public modalBodyHasTopBottomPadding: boolean = false;

	constructor() {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const isSSR = Boolean(process.env.REACT_SSR);

		if (!isSSR) {
			this._resizeObserver = new ResizeObserver((entries) => {
				entries.forEach((entry) => {
					this._currentHeight = entry.contentRect.height;
					if (this._canRecordCurrentHeight) {
						this.recordCurrentContentHeight();
					}
				});
			});

			const windowHeight = window.innerHeight || (window.top?.innerHeight ?? 0);
			this.modalBodyHasTopBottomPadding = windowHeight > 888;
		}
	}

	public startObservingDialogHeight = (node: HTMLElement) => {
		if (node) {
			this._observeTarget = node;
			this._resizeObserver?.observe(this._observeTarget);
		}
	};

	public stopObservingDialogHeight = () => {
		this._currentHeight = 0;
		this._lastRecordedContentHeight = 0;

		if (this._observeTarget) {
			this._resizeObserver?.unobserve(this._observeTarget);
		}
	};

	public recordCurrentContentHeight = () => {
		this._lastRecordedContentHeight = this._currentHeight;
	};

	public setCanRecordCurrentHeight = (canRecordCurrentHeight: boolean) => {
		this._canRecordCurrentHeight = canRecordCurrentHeight;

		if (canRecordCurrentHeight) {
			this.recordCurrentContentHeight();
		}
	};

	public get accessRequestsLoadingHeight(): number {
		// Header above the access requests table is 72px, so need to subtract it
		const modalHeaderAdjustment = 72;

		// The Access Requests dialog adds a 2px negative margin when the body has top/bottom padding
		// This is to counteract the padding that gets added to the ModalBody when the window height is above 888px
		const modalBodyPaddingAdjustment: number = this.modalBodyHasTopBottomPadding ? 0 : 2;
		const finalHeight =
			this._lastRecordedContentHeight - modalHeaderAdjustment + modalBodyPaddingAdjustment;

		// We don't want the height to exceed 416px (8 access requests visible), so we cap it
		return Math.min(finalHeight, 416);
	}

	public getUsdLoadingHeight = ({
		specificAccessVisible,
		isDraft,
		isPublishModalFlow,
	}: UsdLoadingHeightContext) => {
		// When we do subsequent reloads, we will have a previous height recorded
		if (this._lastRecordedContentHeight > 0) {
			return this._lastRecordedContentHeight;
		}

		// On the very first load we need to manually set the height
		const baseLoadingHeight = specificAccessVisible ? 411 : 319;

		// Drafts have an extra message we need to account for
		// That message gets removed if Specific Access is visible
		const draftMessageAdjustment: number =
			isDraft && !specificAccessVisible && !isPublishModalFlow ? 55 : 0;

		const publishModalFlowAdjustment: number = isPublishModalFlow ? 67 : 0;

		// if window height is above 888, an extra 4px padding gets slapped on the ModalBody
		const modalBodyPaddingAdjustment: number =
			specificAccessVisible && this.modalBodyHasTopBottomPadding ? 4 : 0;

		// ToDo: Handle other cases like free edition upsells, embeds, etc etc
		// Store this pre-determined height early, this way access requests can also use it
		this._lastRecordedContentHeight =
			baseLoadingHeight +
			draftMessageAdjustment +
			publishModalFlowAdjustment +
			modalBodyPaddingAdjustment;

		return this._lastRecordedContentHeight;
	};
}

const USD_LOADING_HEIGHT_INSTANCE = new UsdLoadingHeight();
export const useUsdLoadingHeight = () => USD_LOADING_HEIGHT_INSTANCE;
